import React, { Component } from 'react';
import Modal from 'react-bootstrap/lib/Modal';
import Button from 'react-bootstrap/lib/Button';
import styles from './backupModals.module.css';

import importWhiteIcon from '../../../common/Images/import_file_white.png';
import {
    BACKUP_RESTORE_DATA_OPTIONS,
    BACKUP_RESTORE_TYPES,
    BACKUP_STATUS,
    HTTP_OPTIONS
} from '@silverfort-nodelibs/common/utils/consts';
import SquareRadio from '../../../Controls/Buttons/SquareRadio/SquareRadio';
import { httpGet, httpPost } from '../../../utils';
import Input from '../Controls/Input/Input';

class ImportBackup extends Component {
    state = {
        loading: false,
        completed: false,
        selectedFile: null,
        error: null,
        success: null,
        backupRestoreType: BACKUP_RESTORE_TYPES.SETTINGS,
        waitingDots: '',
        dataRestoreOptions: {}
    };

    fileChangedHandler = (file) => {
        const maxFileSize = this.props.maxFileSize ? this.props.maxFileSize : 512000000;
        const maxFileSizeText = this.props.maxFileSizeText ? this.props.maxFileSizeText : '500 MB';
        if (file.size > maxFileSize) {
            this.setState({ error: `File exceeds maximum size of ${maxFileSizeText}`, loading: false });
        } else if (!file.name.endsWith('.zip')) {
            this.setState({ error: `Wrong file format`, loading: false });
        } else {
            this.setState({ selectedFile: file, error: null, loading: false });
        }
    };

    importClickedHandler = () => {
        const fileData = new FormData();
        fileData.append('file', this.state.selectedFile);
        fileData.append('backupType', this.state.backupRestoreType);
        fileData.append(
            'backupDataOptions',
            Object.keys(this.state.dataRestoreOptions)
                .filter((k) => this.state.dataRestoreOptions[k])
                .map((k) => BACKUP_RESTORE_DATA_OPTIONS[k])
                .toString()
        );

        this.state.password && fileData.append('password', this.state.password);

        this.setState({ success: null, error: null, loading: true });
        this.renderWaitingDots();
        const reader = new FileReader();
        reader.onload = async () => {
            const res = await httpPost('/Settings/ImportSystemBackup', fileData, {
                [HTTP_OPTIONS.HANDLE_ERRORS]: true
            });

            if (res.error) {
                if (res.status === 400) {
                    this.setState({ error: 'Incorrect file password!', loading: false });
                } else if (res.status === 503) {
                    this.setState({ error: 'Previous backup process is still running!', loading: false });
                } else if (res.status === 429) {
                    this.setState({ error: 'Too many requests. Please wait a minute and try again', loading: false });
                } else {
                    this.setState({ error: 'Could not open backup file', loading: false });
                }
            } else {
                const requestedData = {};
                for (const k in this.state.dataRestoreOptions) {
                    if (this.state.dataRestoreOptions[k]) requestedData[BACKUP_RESTORE_DATA_OPTIONS[k]] = true;
                }
                await this.pollBackupStatus(requestedData);
            }
        };

        reader.readAsBinaryString(this.state.selectedFile);
    };

    pollBackupStatus = async (requestedData) => {
        let retries = 5;
        const interval = 3000;
        const token = setInterval(async () => {
            const res = await httpGet('/Settings/getSystemBackupStatus', {
                [HTTP_OPTIONS.HANDLE_ERRORS]: true
            });

            // Since the server restarts once its done, we might poll at this time frame, therefore we try again.
            if (res.error) {
                retries--;
                !retries && this.setState({ error: 'Failed to restore backup', loading: false });
            } else if (res.data.backupStatus === BACKUP_STATUS.READY) {
                this.setState({
                    error: null,
                    success: 'Backup has been restored successfully! Reloading',
                    completed: true,
                    loading: false,
                    waitingDots: ''
                });
                this.renderWaitingDots('completed');
                this.props.onImport();
            } else if (res.data.backupStatus === BACKUP_STATUS.ERROR) {
                if (this.state.backupRestoreType === BACKUP_RESTORE_TYPES.SETTINGS || !res.data.handlersStatus) {
                    this.setState({ error: 'Failed to restore backup', loading: false });
                } else {
                    const handlersResult = { [BACKUP_STATUS.READY]: [], [BACKUP_STATUS.ERROR]: [] };

                    // In case of error while restoring data,
                    // some of the data restoration jobs might still be running,
                    // so we wait until there is no restoration job running
                    for (const data in requestedData) {
                        if (res.data.handlersStatus[data] === BACKUP_STATUS.BUSY) return;
                        handlersResult[res.data.handlersStatus[data]].push(data);
                    }

                    this.setState({
                        error: (
                            <>
                                Data could not be restored
                                <div className={styles.dataFailure}>{`Failed: ${
                                    handlersResult[BACKUP_STATUS.ERROR]
                                }`}</div>
                                <div className={styles.dataSuccess}>{`Succeeded: ${
                                    handlersResult[BACKUP_STATUS.READY].length
                                        ? handlersResult[BACKUP_STATUS.READY]
                                        : 'None'
                                }`}</div>
                            </>
                        ),
                        loading: false
                    });
                }
            }

            if ((res.data && res.data.backupStatus !== BACKUP_STATUS.BUSY) || !retries) {
                clearInterval(token);
            }
        }, interval);
    };

    onDataSelectionChange = (val) => {
        this.setState({ backupRestoreType: val, dataRestoreOptions: {} });
    };

    renderWaitingDots(stopProp = 'loading') {
        const token = setInterval(() => {
            this.setState({ waitingDots: this.state.waitingDots.length === 3 ? '.' : this.state.waitingDots + '.' });
            if (!this.state[stopProp]) clearInterval(token);
        }, 300);
    }

    render() {
        return (
            <Modal
                id="importSilverfortBackup"
                dialogClassName="modal-md"
                show
                onHide={this.props.onClose}
                keyboard={false}
                backdrop="static">
                <Modal.Header closeButton>
                    <Modal.Title>Import Backup</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div
                        className={`${styles.container} ${
                            (this.state.loading || this.state.completed) && styles.loading
                        }`}>
                        <div className={styles.fileUploadSection}>
                            Choose a backup file to upload:
                            <div className={styles.fileSelection}>
                                <div className={styles.filePath}>
                                    <span>{this.state.selectedFile ? this.state.selectedFile.name : ''}</span>
                                </div>
                                <div className={styles.fileButton}>
                                    <label htmlFor="backupImport">
                                        <img src={importWhiteIcon} alt="" />
                                        Browse
                                    </label>
                                    <input
                                        type="file"
                                        id="backupImport"
                                        accept="application/zip"
                                        multiple={false}
                                        disabled={this.state.loading}
                                        onChange={(e) => this.fileChangedHandler(e.target.files[0])}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className={styles.filePasswordSection}>
                            Enter the backup file password
                            <Input
                                type="password"
                                id="backupPassword"
                                maxLength={128}
                                value={this.state.password}
                                disabled={!this.state.selectedFile}
                                changed={(id, val) => val.length <= 128 && this.setState({ password: val })}
                            />
                        </div>
                        <div className={styles.dataRestorationSection}>
                            <div>
                                Choose type of data to restore:
                                {this.state.backupRestoreType === BACKUP_RESTORE_TYPES.DATA && (
                                    <b className={styles.alert}>Please run AD Sync before importing data</b>
                                )}
                                <div className={styles.optionsRadio}>
                                    <SquareRadio
                                        handleSquareRadioChange={(val) => this.onDataSelectionChange(val)}
                                        valuesList={Object.values(BACKUP_RESTORE_TYPES).map((option) => {
                                            return { label: option, value: option };
                                        })}
                                        radioValue={this.state.backupRestoreType}
                                    />
                                </div>
                                {this.state.backupRestoreType === BACKUP_RESTORE_TYPES.DATA &&
                                    Object.keys(BACKUP_RESTORE_DATA_OPTIONS).map((key) => {
                                        return (
                                            <div key={`${key}-option`} className={styles.dataOptions}>
                                                <input
                                                    type="checkbox"
                                                    id={`${key}checkbox`}
                                                    name={key}
                                                    disabled={this.state.loading || this.state.completed}
                                                    checked={this.state.dataRestoreOptions[key]}
                                                    onChange={() =>
                                                        this.setState((prevState) => ({
                                                            dataRestoreOptions: {
                                                                ...prevState.dataRestoreOptions,
                                                                [key]: !prevState.dataRestoreOptions[key]
                                                            }
                                                        }))
                                                    }
                                                />
                                                <label htmlFor={`${key}checkbox`}>
                                                    <span>{BACKUP_RESTORE_DATA_OPTIONS[key]}</span>
                                                </label>
                                            </div>
                                        );
                                    })}
                            </div>
                        </div>
                        <div className={styles.infoSection}>(It may take several minutes to upload all data)</div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <div className={styles.footer}>
                        <div className={styles.messages}>
                            {this.state.loading && (
                                <span>
                                    Importing backup, please wait
                                    <span className={styles.waitingDots}>{this.state.waitingDots}</span>
                                </span>
                            )}
                            {this.state.error && <span className={styles.error}>{this.state.error}</span>}
                            {this.state.success && (
                                <span className={styles.success}>
                                    {this.state.success}
                                    <span className={styles.waitingDots}>{this.state.waitingDots}</span>
                                </span>
                            )}
                        </div>
                        <div className={styles.controls}>
                            <Button disabled={this.state.loading || this.state.completed} onClick={this.props.onClose}>
                                Close
                            </Button>
                            <Button
                                bsStyle={'success'}
                                disabled={
                                    !this.state.selectedFile ||
                                    this.state.completed ||
                                    this.state.loading ||
                                    (this.state.backupRestoreType === BACKUP_RESTORE_TYPES.DATA &&
                                        !Object.values(this.state.dataRestoreOptions).filter((v) => v).length)
                                }
                                onClick={this.importClickedHandler}>
                                Import
                            </Button>
                        </div>
                    </div>
                </Modal.Footer>
            </Modal>
        );
    }
}

export default ImportBackup;
